// SaveAsTemplateButton — declared below in same file

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../auth";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PortalHeader } from "../components/PortalHeader";
import { TcmDatePicker } from "../components/TcmDatePicker";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <p className="tcm-eyebrow mb-3">{title}</p>
    {children}
  </section>
);

export default function AdminTripEditor() {
  const { tripId } = useParams();
  const { API, authHeaders, user } = useAuth();
  const nav = useNavigate();
  const [trip, setTrip] = useState(null);
  const [it, setIt] = useState([]);
  const [res, setRes] = useState([]);
  const [budget, setBudget] = useState([]);
  const [log, setLog] = useState([]);
  const [rec, setRec] = useState([]);
  const [upd, setUpd] = useState([]);

  const load = async () => {
    const H = { headers: authHeaders };
    setTrip((await axios.get(`${API}/trips/${tripId}`, H)).data);
    setIt((await axios.get(`${API}/trips/${tripId}/itinerary`, H)).data);
    setRes((await axios.get(`${API}/trips/${tripId}/reservations`, H)).data);
    setBudget((await axios.get(`${API}/trips/${tripId}/budget`, H)).data);
    setLog((await axios.get(`${API}/trips/${tripId}/logistics`, H)).data);
    setRec((await axios.get(`${API}/trips/${tripId}/explore`, H)).data);
    setUpd((await axios.get(`${API}/trips/${tripId}/updates`, H)).data);
  };
  useEffect(() => { load(); }, [tripId]);

  const post = (path, body) => axios.post(`${API}${path}`, body, { headers: authHeaders }).then(load).catch(() => toast.error("Failed"));
  const del = (path) => axios.delete(`${API}${path}`, { headers: authHeaders }).then(load);

  if (!trip) return <p className="p-10 tcm-eyebrow">Loading...</p>;

  return (
    <div className="min-h-screen" data-testid="admin-trip-editor">
      <PortalHeader eyebrow={`${trip.destination} · Editor`} backTo="/admin" />
      <div className="px-6 py-10 max-w-4xl mx-auto">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <p className="tcm-eyebrow">{trip.client?.full_name} · {trip.currency}</p>
          <h1 className="font-display text-4xl mt-2">{trip.destination}</h1>
          <p className="text-sm mt-1 text-[color:var(--tcm-navy-70)]">{trip.start_date} → {trip.end_date}</p>
        </div>
        <SaveAsTemplateButton tripId={tripId} />
      </div>
      {user?.role === "admin" && (
        <DeleteTripButton tripId={tripId} destination={trip.destination} onDeleted={() => nav("/admin")} />
      )}
      <hr className="tcm-gold-rule mb-8" />

      <Section title="Live update (fires notification)">
        <MiniForm testid="update" fields={[["title", "Title"], ["body", "Message"]]}
                  onSubmit={(v) => post(`/trips/${tripId}/updates`, v)} label="Post update" />
        <ul className="mt-4 space-y-2">
          {upd.map((u) => <li key={u.id} className="border-t border-[color:var(--tcm-navy-15)] pt-2">
            <p className="font-display text-lg">{u.title}</p><p className="text-sm">{u.body}</p></li>)}
        </ul>
      </Section>

      <Section title="Itinerary">
        <MiniForm testid="itinerary" fields={[["day", "Day #", "number"], ["time", "Time"], ["title", "Title"], ["description", "Description"], ["location", "Location"]]}
                  extras={[["magic_touch", "Magic Touch (hidden until day)"]]}
                  onSubmit={(v) => post(`/trips/${tripId}/itinerary`, { ...v, day: parseInt(v.day) })} label="Add item" />
        <ul className="mt-4 space-y-2">
          {it.map((x) => <li key={x.id} className="border-t border-[color:var(--tcm-navy-15)] pt-2 flex justify-between">
            <span>Day {x.day} · {x.title}{x.magic_touch ? " ✨" : ""}</span>
            <button data-testid={`del-it-${x.id}`} onClick={() => del(`/itinerary/${x.id}`)} className="tcm-eyebrow">Remove</button></li>)}
        </ul>
      </Section>

      <Section title="Budget & Payments">
        <MiniForm testid="budget" fields={[["label", "Label"], ["amount", "Amount", "number"]]}
                  selects={[["type", "Type", ["tcm_fee", "supplier"]], ["currency", "Currency", ["EUR", "GBP"]]]}
                  optional={[["supplier_name", "Supplier name"], ["supplier_link", "Supplier link"], ["bank_details", "Bank details (multiline)"]]}
                  onSubmit={(v) => post(`/trips/${tripId}/budget`, { ...v, amount: parseFloat(v.amount) })} label="Add line" />
        <ul className="mt-4 space-y-2">
          {budget.map((b) => <li key={b.id} className="border-t border-[color:var(--tcm-navy-15)] pt-2 flex justify-between">
            <span>{b.type} · {b.label} · {b.currency} {b.amount} · <em>{b.status}</em></span>
            <button data-testid={`del-b-${b.id}`} onClick={() => del(`/budget/${b.id}`)} className="tcm-eyebrow">Remove</button></li>)}
        </ul>
      </Section>

      <Section title="Reservations">
        <MiniForm testid="res" fields={[["title", "Title"], ["reference", "Reference"], ["date", "Date", "date"]]}
                  onSubmit={(v) => post(`/trips/${tripId}/reservations`, v)} label="Add reservation" />
        <ul className="mt-4 space-y-2">{res.map((r) => <li key={r.id} className="border-t border-[color:var(--tcm-navy-15)] pt-2 flex justify-between">
          <span>{r.title}{r.date ? ` · ${r.date}` : ""} · {r.reference || "-"}</span>
          <button onClick={() => del(`/reservations/${r.id}`)} className="tcm-eyebrow">Remove</button></li>)}</ul>
      </Section>

      <AssignStaffSection tripId={tripId} />

      <AddonsSection tripId={tripId} currency={trip.currency} />

      <ExperiencesSection tripId={tripId} currency={trip.currency} />

      <Section title="Logistics">
        <MiniForm testid="log" fields={[["title", "Title"], ["details", "Details"]]}
                  selects={[["category", "Category", ["transfer", "access", "contact"]]]}
                  onSubmit={(v) => post(`/trips/${tripId}/logistics`, v)} label="Add" />
        <ul className="mt-4 space-y-2">{log.map((l) => <li key={l.id} className="border-t border-[color:var(--tcm-navy-15)] pt-2 flex justify-between">
          <span>{l.category} · {l.title}</span>
          <button onClick={() => del(`/logistics/${l.id}`)} className="tcm-eyebrow">Remove</button></li>)}</ul>
      </Section>

      <Section title="Explore (curated recs)">
        <MiniForm testid="rec" fields={[["title", "Title"], ["category", "Category"], ["description", "Description"], ["address", "Address"], ["map_link", "Map link"]]}
                  onSubmit={(v) => post(`/trips/${tripId}/explore`, v)} label="Add rec" />
        <ul className="mt-4 space-y-2">{rec.map((r) => <li key={r.id} className="border-t border-[color:var(--tcm-navy-15)] pt-2 flex justify-between">
          <span>{r.title}</span>
          <button onClick={() => del(`/explore/${r.id}`)} className="tcm-eyebrow">Remove</button></li>)}</ul>
      </Section>
      </div>
    </div>
  );
}

function MiniForm({ testid, fields = [], selects = [], optional = [], extras = [], onSubmit, label }) {
  const [v, setV] = useState({});
  const submit = (e) => { e.preventDefault(); onSubmit(v); setV({}); };
  return (
    <form onSubmit={submit} data-testid={`form-${testid}`} className="tcm-double-frame">
      <div className="tcm-inner p-4 space-y-3">
        {fields.map(([k, l, t]) => (
          <div key={k}><label className="tcm-eyebrow block mb-1">{l}</label>
            {t === "date" ? (
              <TcmDatePicker testId={`in-${testid}-${k}`} value={v[k] || ""} onChange={(d) => setV({ ...v, [k]: d })} />
            ) : (
              <input data-testid={`in-${testid}-${k}`} type={t || "text"} className="tcm-input" value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} required />
            )}</div>
        ))}
        {selects.map(([k, l, opts]) => (
          <div key={k}><label className="tcm-eyebrow block mb-1">{l}</label>
            <select data-testid={`sel-${testid}-${k}`} className="tcm-input" value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} required>
              <option value="">Select</option>
              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></div>
        ))}
        {optional.map(([k, l]) => (
          <div key={k}><label className="tcm-eyebrow block mb-1">{l}</label>
            {k === "bank_details" ?
              <textarea className="tcm-input" rows="3" value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
              : <input className="tcm-input" value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} />}</div>
        ))}
        {extras.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 tcm-eyebrow">
            <input type="checkbox" checked={!!v[k]} onChange={(e) => setV({ ...v, [k]: e.target.checked })} /> {l}
          </label>
        ))}
        <button data-testid={`submit-${testid}`} className="tcm-btn-primary">{label}</button>
      </div>
    </form>
  );
}

function SaveAsTemplateButton({ tripId }) {
  const { API, authHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/trips/${tripId}/save-as-template`,
        { name, description }, { headers: authHeaders });
      toast.success("Template saved");
      setOpen(false); setName(""); setDescription("");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button data-testid="save-template-btn" onClick={() => setOpen(true)} className="tcm-btn-outline">
        Save as template
      </button>
    );
  }
  return (
    <form onSubmit={save} className="tcm-double-frame w-80">
      <div className="tcm-inner p-4 space-y-3">
        <p className="tcm-eyebrow">Save as template</p>
        <input data-testid="template-name" className="tcm-input" placeholder="Template name"
               value={name} onChange={(e) => setName(e.target.value)} required />
        <input data-testid="template-desc" className="tcm-input" placeholder="Short description (optional)"
               value={description} onChange={(e) => setDescription(e.target.value)} />
        <div className="flex gap-2">
          <button data-testid="template-save-submit" disabled={busy} className="tcm-btn-primary flex-1">
            {busy ? "Saving..." : "Save"}
          </button>
          <button type="button" onClick={() => setOpen(false)} className="tcm-btn-outline">Cancel</button>
        </div>
      </div>
    </form>
  );
}

function DeleteTripButton({ tripId, destination, onDeleted }) {
  const { API, authHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const expected = destination || "DELETE";
  const canDelete = confirm.trim() === expected;

  const run = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/trips/${tripId}`, { headers: authHeaders });
      toast.success("Trip deleted");
      onDeleted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="mb-6 flex justify-end">
        <button data-testid="trip-delete-open"
                onClick={() => setOpen(true)}
                className="text-[11px] tracking-[0.14em] uppercase py-2 px-3 border border-[#8B2E2E] text-[#8B2E2E] hover:bg-[#8B2E2E] hover:text-[color:var(--tcm-cream)]">
          Delete trip
        </button>
      </div>
    );
  }
  return (
    <div className="mb-6 tcm-double-frame" data-testid="trip-delete-panel">
      <div className="tcm-inner p-5 space-y-3">
        <p className="tcm-eyebrow" style={{ color: "#8B2E2E" }}>Danger zone</p>
        <p className="text-[13.5px] leading-[1.55]">
          This permanently deletes <strong>{destination}</strong> and every piece of trip data —
          itinerary, budget lines, reservations, messages, diary, documents, memory-book orders,
          and finance notifications. The client's profile is kept.
        </p>
        <p className="text-[12px] text-[color:var(--tcm-navy-70)]">
          To confirm, type the trip destination <code className="tcm-eyebrow">{expected}</code> below.
        </p>
        <input
          data-testid="trip-delete-confirm"
          className="tcm-input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={expected}
          autoFocus
        />
        <div className="flex gap-2 pt-2">
          <button data-testid="trip-delete-cancel"
                  onClick={() => { setOpen(false); setConfirm(""); }}
                  className="tcm-btn-outline">Cancel</button>
          <button data-testid="trip-delete-confirm-btn"
                  disabled={!canDelete || busy}
                  onClick={run}
                  className="flex-1 text-[11px] tracking-[0.2em] uppercase py-3 px-5 border transition-colors"
                  style={{
                    background: canDelete ? "#8B2E2E" : "transparent",
                    color: canDelete ? "var(--tcm-cream)" : "#8B2E2E",
                    borderColor: "#8B2E2E",
                    opacity: busy ? 0.6 : 1,
                  }}>
            {busy ? "Deleting..." : `Permanently delete "${destination}"`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------- Staff assignment --------------------------------
function AssignStaffSection({ tripId }) {
  const { API, authHeaders, user } = useAuth();
  const [team, setTeam] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const t = await axios.get(`${API}/admin/staff`, { headers: authHeaders });
      setTeam(t.data || []);
    } catch { setTeam([]); }
    try {
      const d = await axios.get(`${API}/admin/clients/${tripId}/detail`, { headers: authHeaders });
      setAssigned((d.data.staff || []).map((s) => s.id));
    } catch { setAssigned([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tripId]);

  const toggle = (uid) => {
    setAssigned((cur) => cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);
  };
  const save = async () => {
    if (user?.role !== "admin") { toast.error("Only admins can assign staff"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/trips/${tripId}/assign-staff`,
        { staff_ids: assigned }, { headers: authHeaders });
      toast.success("Staff updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const activeTeam = team.filter((t) => t.active !== false);
  return (
    <Section title="Staff & Owners">
      <p className="text-[13px] text-[color:var(--tcm-navy-70)] mb-3">
        Assign one or more team members to own this trip. Owners are shown on the client's trip page
        and on the admin dashboard trip card.
      </p>
      {activeTeam.length === 0 ? (
        <p className="text-sm text-[color:var(--tcm-navy-70)]">No team members yet. Invite them via the Team page.</p>
      ) : (
        <div className="space-y-2">
          {activeTeam.map((t) => {
            const on = assigned.includes(t.id);
            return (
              <label key={t.id}
                     data-testid={`assign-staff-${t.id}`}
                     className={`flex items-center justify-between gap-3 cursor-pointer border p-3 ${on ? "border-[color:var(--tcm-navy)] bg-[color:var(--tcm-navy)] text-[color:var(--tcm-cream)]" : "border-[color:var(--tcm-navy-15)]"}`}>
                <div className="min-w-0">
                  <p className="font-display text-[16px]">{t.full_name}</p>
                  <p className="text-[11px] opacity-70">{t.email} · {t.role}</p>
                </div>
                <input type="checkbox" className="scale-125" checked={on} onChange={() => toggle(t.id)} />
              </label>
            );
          })}
          <button data-testid="assign-staff-save" onClick={save} disabled={busy || user?.role !== "admin"}
                  className="tcm-btn-primary mt-3">
            {busy ? "Saving..." : user?.role === "admin" ? "Save assignments" : "Admin only"}
          </button>
        </div>
      )}
    </Section>
  );
}

// ------------------------------- Add-ons -----------------------------------
function AddonsSection({ tripId, currency }) {
  const { API, authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [v, setV] = useState({ create_budget_line: true });

  const load = () => axios.get(`${API}/trips/${tripId}/addons`, { headers: authHeaders })
    .then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tripId]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/trips/${tripId}/addons`, {
        ...v, amount: v.amount ? parseFloat(v.amount) : null,
        currency: v.currency || currency,
      }, { headers: authHeaders });
      setV({ create_budget_line: true });
      toast.success("Add-on added");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const remove = async (aid) => {
    try { await axios.delete(`${API}/addons/${aid}`, { headers: authHeaders }); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Section title="Add-ons (bespoke requests)">
      <p className="text-[13px] text-[color:var(--tcm-navy-70)] mb-3">
        Standalone extras — welcome flowers, hair & makeup, private pastry, airport fast-track. Each
        can optionally mirror into the budget so it rolls into totals and the client's "I've paid" flow.
      </p>
      <form onSubmit={add} data-testid="form-addon" className="tcm-double-frame">
        <div className="tcm-inner p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="addon-title" required placeholder="Title · e.g. Welcome flowers"
                   className="tcm-input" value={v.title || ""} onChange={(e) => setV({...v, title: e.target.value})} />
            <input placeholder="Category · flowers, hair & makeup, …"
                   className="tcm-input" value={v.category || ""} onChange={(e) => setV({...v, category: e.target.value})} />
            <input placeholder="Supplier name"
                   className="tcm-input" value={v.supplier_name || ""} onChange={(e) => setV({...v, supplier_name: e.target.value})} />
            <div className="grid grid-cols-[1fr_90px] gap-2">
              <input type="number" step="0.01" placeholder="Amount"
                     data-testid="addon-amount"
                     className="tcm-input" value={v.amount || ""} onChange={(e) => setV({...v, amount: e.target.value})} />
              <select className="tcm-input" value={v.currency || currency} onChange={(e) => setV({...v, currency: e.target.value})}>
                <option>EUR</option><option>GBP</option>
              </select>
            </div>
            <input placeholder="Website" className="tcm-input" value={v.website || ""} onChange={(e) => setV({...v, website: e.target.value})} />
            <input placeholder="Instagram @" className="tcm-input" value={v.instagram || ""} onChange={(e) => setV({...v, instagram: e.target.value})} />
            <input placeholder="Brochure URL" className="tcm-input col-span-2" value={v.brochure_url || ""} onChange={(e) => setV({...v, brochure_url: e.target.value})} />
          </div>
          <textarea placeholder="Notes" rows={2} className="tcm-input" value={v.notes || ""} onChange={(e) => setV({...v, notes: e.target.value})} />
          <label className="flex items-center gap-2 tcm-eyebrow">
            <input type="checkbox" data-testid="addon-mirror-budget"
                   checked={!!v.create_budget_line}
                   onChange={(e) => setV({...v, create_budget_line: e.target.checked})} />
            Mirror into budget (creates a supplier line)
          </label>
          <button data-testid="addon-submit" className="tcm-btn-primary">Add add-on</button>
        </div>
      </form>
      <ul className="mt-4 space-y-3">
        {items.map((a) => (
          <li key={a.id} data-testid={`addon-${a.id}`} className="border border-[color:var(--tcm-navy-15)] p-4">
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <p className="font-display text-[18px]">{a.title}</p>
                {a.category && <p className="tcm-eyebrow">{a.category}</p>}
                {a.supplier_name && <p className="text-[13px] mt-1">{a.supplier_name}</p>}
                {a.amount && <p className="text-[13px] font-medium">{a.currency || currency} {Number(a.amount).toFixed(2)}</p>}
                <div className="flex flex-wrap gap-3 mt-2 text-[12px]">
                  {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">Website</a>}
                  {a.instagram && <a href={`https://instagram.com/${a.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">Instagram</a>}
                  {a.brochure_url && <a href={a.brochure_url} target="_blank" rel="noreferrer" className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">Brochure</a>}
                </div>
                {a.notes && <p className="text-[12px] mt-2 text-[color:var(--tcm-navy-70)]">{a.notes}</p>}
              </div>
              <button data-testid={`addon-del-${a.id}`} onClick={() => remove(a.id)} className="tcm-eyebrow">Remove</button>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// ----------------------------- Experiences ---------------------------------
function ExperiencesSection({ tripId, currency }) {
  const { API, authHeaders } = useAuth();
  const [experiences, setExperiences] = useState([]);
  const [newExp, setNewExp] = useState({ title: "", date: "", notes: "" });

  const load = () => axios.get(`${API}/trips/${tripId}/experiences`, { headers: authHeaders })
    .then((r) => setExperiences(r.data || [])).catch(() => setExperiences([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tripId]);

  const addExp = async (e) => {
    e.preventDefault();
    if (!newExp.title.trim()) return;
    try {
      await axios.post(`${API}/trips/${tripId}/experiences`, newExp, { headers: authHeaders });
      setNewExp({ title: "", date: "", notes: "" });
      load();
    } catch (err) { toast.error("Failed"); }
  };
  const delExp = async (eid) => {
    try { await axios.delete(`${API}/experiences/${eid}`, { headers: authHeaders }); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Section title="Experiences (sub-productions)">
      <p className="text-[13px] text-[color:var(--tcm-navy-70)] mb-3">
        Group an event inside the trip — a birthday dinner, a shoot, a wellness afternoon — with its own
        list of vendors: photographer, videographer, chef, florist, hair, MUA, …
      </p>
      <form onSubmit={addExp} data-testid="form-experience" className="tcm-double-frame">
        <div className="tcm-inner p-4 space-y-3">
          <input data-testid="exp-title" required placeholder="Experience title · e.g. Sarah's 40th dinner"
                 className="tcm-input" value={newExp.title} onChange={(e) => setNewExp({...newExp, title: e.target.value})} />
          <div className="grid grid-cols-[200px_1fr] gap-3">
            <TcmDatePicker testId="exp-date" value={newExp.date} onChange={(d) => setNewExp({...newExp, date: d})} placeholder="Date (optional)" />
            <input placeholder="Short notes" className="tcm-input" value={newExp.notes} onChange={(e) => setNewExp({...newExp, notes: e.target.value})} />
          </div>
          <button data-testid="exp-submit" className="tcm-btn-primary">Create experience</button>
        </div>
      </form>
      <div className="mt-4 space-y-4">
        {experiences.map((e) => (
          <ExperienceCard key={e.id} exp={e} currency={currency} onDelete={() => delExp(e.id)} onChange={load} />
        ))}
      </div>
    </Section>
  );
}

function ExperienceCard({ exp, currency, onDelete, onChange }) {
  const { API, authHeaders, token } = useAuth();
  const [v, setV] = useState({ create_budget_line: true });
  const [sending, setSending] = useState(false);

  const addVendor = async (e) => {
    e.preventDefault();
    if (!v.name?.trim()) return;
    try {
      await axios.post(`${API}/experiences/${exp.id}/vendors`, {
        ...v, cost: v.cost ? parseFloat(v.cost) : null,
        currency: v.currency || currency,
      }, { headers: authHeaders });
      setV({ create_budget_line: true });
      toast.success("Vendor added");
      onChange();
    } catch { toast.error("Failed"); }
  };
  const delVendor = async (vid) => {
    try { await axios.delete(`${API}/experience-vendors/${vid}`, { headers: authHeaders }); onChange(); }
    catch { toast.error("Failed"); }
  };
  const emailDaySheet = async () => {
    if (!window.confirm("Email the day sheet PDF to the client now?")) return;
    setSending(true);
    try {
      await axios.post(`${API}/experiences/${exp.id}/email-day-sheet`, {}, { headers: authHeaders });
      toast.success("Day sheet emailed to client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <div className="border border-[color:var(--tcm-navy)] p-5" data-testid={`experience-${exp.id}`}>
      <div className="flex justify-between items-start gap-3 mb-3">
        <div>
          <p className="tcm-eyebrow">{exp.date || "No date set"}</p>
          <h3 className="font-display text-[22px]">{exp.title}</h3>
          {exp.notes && <p className="text-[13px] mt-1 text-[color:var(--tcm-navy-70)]">{exp.notes}</p>}
        </div>
        <button data-testid={`exp-del-${exp.id}`} onClick={onDelete} className="tcm-eyebrow">Remove experience</button>
      </div>
      <div className="flex flex-wrap gap-3 mb-4" data-testid={`exp-actions-${exp.id}`}>
        <a href={`${API}/experiences/${exp.id}/day-sheet.pdf?auth=${token}`}
           target="_blank" rel="noreferrer"
           data-testid={`exp-pdf-${exp.id}`}
           className="text-[10.5px] tracking-[0.14em] uppercase py-1.5 px-2.5 border border-[color:var(--tcm-navy)]">
          Day sheet PDF
        </a>
        <button type="button" onClick={emailDaySheet} disabled={sending}
                data-testid={`exp-email-${exp.id}`}
                className="text-[10.5px] tracking-[0.14em] uppercase py-1.5 px-2.5 border border-[color:var(--tcm-gold)] text-[color:var(--tcm-gold-700)] disabled:opacity-50">
          {sending ? "Sending…" : "Email to client"}
        </button>
      </div>
      <hr className="tcm-gold-rule w-10 my-3" />

      {/* Vendor cards */}
      <ul className="space-y-3">
        {(exp.vendors || []).map((vn) => (
          <li key={vn.id} data-testid={`vendor-${vn.id}`} className="border border-[color:var(--tcm-navy-15)] p-3">
            <div className="flex justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-display text-[16px]">{vn.name}</p>
                <p className="tcm-eyebrow">{vn.role || "vendor"}</p>
                {vn.cost && <p className="text-[13px] font-medium mt-1">{vn.currency || currency} {Number(vn.cost).toFixed(2)}</p>}
                <div className="flex flex-wrap gap-3 mt-2 text-[12px]">
                  {vn.phone && <a href={`tel:${vn.phone}`} className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">Call</a>}
                  {vn.email && <a href={`mailto:${vn.email}`} className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">Email</a>}
                  {vn.website && <a href={vn.website} target="_blank" rel="noreferrer" className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">Website</a>}
                  {vn.instagram && <a href={`https://instagram.com/${vn.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">Instagram</a>}
                  {vn.brochure_url && <a href={vn.brochure_url} target="_blank" rel="noreferrer" className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">Brochure</a>}
                </div>
                {vn.notes && <p className="text-[12px] mt-2 text-[color:var(--tcm-navy-70)]">{vn.notes}</p>}
              </div>
              <button data-testid={`vendor-del-${vn.id}`} onClick={() => delVendor(vn.id)} className="tcm-eyebrow">Remove</button>
            </div>
          </li>
        ))}
      </ul>

      {/* Add vendor form */}
      <form onSubmit={addVendor} data-testid={`form-vendor-${exp.id}`} className="mt-4 tcm-double-frame">
        <div className="tcm-inner p-4 space-y-3">
          <p className="tcm-eyebrow">Add vendor</p>
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Name" data-testid={`vendor-name-${exp.id}`} className="tcm-input" value={v.name || ""} onChange={(e) => setV({...v, name: e.target.value})} />
            <input placeholder="Role · photographer, chef, florist, MUA, hair, videographer, …" className="tcm-input" value={v.role || ""} onChange={(e) => setV({...v, role: e.target.value})} />
            <input placeholder="Phone" className="tcm-input" value={v.phone || ""} onChange={(e) => setV({...v, phone: e.target.value})} />
            <input placeholder="Email" className="tcm-input" value={v.email || ""} onChange={(e) => setV({...v, email: e.target.value})} />
            <input placeholder="Website" className="tcm-input" value={v.website || ""} onChange={(e) => setV({...v, website: e.target.value})} />
            <input placeholder="Instagram @" className="tcm-input" value={v.instagram || ""} onChange={(e) => setV({...v, instagram: e.target.value})} />
            <input placeholder="Brochure URL" className="tcm-input col-span-2" value={v.brochure_url || ""} onChange={(e) => setV({...v, brochure_url: e.target.value})} />
            <div className="grid grid-cols-[1fr_90px] gap-2">
              <input type="number" step="0.01" placeholder="Cost (optional)" className="tcm-input" value={v.cost || ""} onChange={(e) => setV({...v, cost: e.target.value})} />
              <select className="tcm-input" value={v.currency || currency} onChange={(e) => setV({...v, currency: e.target.value})}>
                <option>EUR</option><option>GBP</option>
              </select>
            </div>
          </div>
          <textarea placeholder="Notes" rows={2} className="tcm-input" value={v.notes || ""} onChange={(e) => setV({...v, notes: e.target.value})} />
          <label className="flex items-center gap-2 tcm-eyebrow">
            <input type="checkbox" checked={!!v.create_budget_line} onChange={(e) => setV({...v, create_budget_line: e.target.checked})} />
            Mirror into budget (creates a supplier line linked to this vendor)
          </label>
          <button data-testid={`vendor-submit-${exp.id}`} className="tcm-btn-primary">Add vendor</button>
        </div>
      </form>
    </div>
  );
}


